import { _$ } from "/WebInterface/new-ui/assets/js/utils/dom.js";
import { toast } from "/WebInterface/new-ui/modules/toast.js";
import store from "/WebInterface/new-ui/store/index.js";
import { t } from "/WebInterface/new-ui/modules/localizations/localizations.js";
import { fileTypes } from "/WebInterface/new-ui/assets/js/constants.js";

export class ImagePreview {
  constructor({ previewInfo, parent }) {
    const { href_path, mediaInfo, preview } = previewInfo;

    const source =
      preview > 0
        ? `/?command=getPreview&size=3&path=${href_path}&frame=1&c2f=${utils.cookie.get(
            "currentAuth"
          )}`
        : `/?command=download&mimeType=${
            mediaInfo.mimeType
          }&path=${href_path}&c2f=${utils.cookie.get("currentAuth")}`;

    this.loader = _$("<div>").addClass("media-loader").el();
    _$(parent).append(this.loader);

    this.image = _$("<img>").attr("src", source).el();
    _$(parent).append(this.image);

    _$(this.image).on("load", () => {
      const height = this.image.height;
      const width = this.image.width;

      const orientation = height > width ? "portrait" : "landscape";
      _$(this.image).addClass(orientation);
    });

    this.showLoader(); // Show loader immediately
    this.setupEventListeners();
  }

  setupEventListeners() {
    this.image.addEventListener("load", () => this.hideLoader());
    this.image.addEventListener("error", () => this.handleImageError());
  }

  showLoader() {
    _$(this.loader).show();
  }

  hideLoader() {
    _$(this.loader).hide();
  }

  handleImageError() {
    console.log("Image loading failed");
    this.hideLoader();
    // You can add additional error handling here if needed
  }

  showLoader() {
    _$(this.loader).show();
  }

  hideLoader() {
    _$(this.loader).hide();
  }

  resetZoom = () => {
    const { image } = this;
    const height = image.clientHeight;
    // Round dynamic values to nearest 50px for utility class compatibility
    const roundedMaxHeight = Math.round((height + 200) / 50) * 50;
    _$(image).css("height", "auto");
    _$(image).css("maxHeight", `${roundedMaxHeight}px`);
    _$(image).css("maxWidth", "none");
  };

  setZoom = (zoomFactor) => {
    const { image } = this;
    const originalHeight = image.naturalHeight;
    const newHeight = originalHeight * zoomFactor;
    // Round dynamic values to nearest 50px for utility class compatibility
    const roundedHeight = Math.round(newHeight / 50) * 50;
    _$(image).css("height", `${roundedHeight}px`);
    _$(image).css("maxHeight", `${roundedHeight}px`);
    _$(image).css("maxWidth", "none");
  };

  zoomIn = () => {
    const { image } = this;
    const height = image.clientHeight;
    // Round dynamic values to nearest 50px for utility class compatibility
    const roundedHeight = Math.round((height + 200) / 50) * 50;
    _$(image).css("height", `${roundedHeight}px`);
    _$(image).css("maxHeight", `${roundedHeight}px`);
    _$(image).css("maxWidth", "none");
  };

  zoomOut = () => {
    const { image } = this;
    const height = image.clientHeight;
    // Round dynamic values to nearest 50px for utility class compatibility
    if (height - 150 < 100) {
      _$(image).css("height", "100px");
    } else {
      const roundedHeight = Math.round((height - 150) / 50) * 50;
      _$(image).css("height", `${roundedHeight}px`);
    }
  };

  rotateImage = (direction) => {
    const { image } = this;

    // Get current rotation from CSS custom property or computed style
    const currentRotation = image.style.getPropertyValue("--image-rotation") || 
                           getComputedStyle(image).getPropertyValue("--image-rotation") ||
                           "0deg";
    
    // Extract numeric value from rotation string (e.g., "90deg" -> 90)
    const currentDeg = Number(currentRotation.replace("deg", "")) || 0;

    // Set transition duration via CSS custom property (CSP-compliant)
    image.style.setProperty("--image-transition-duration", "0.3s");

    // Calculate new rotation
    let newDeg = currentDeg;
    if (direction === "left") {
      newDeg = currentDeg - 90;
    } else if (direction === "right") {
      newDeg = currentDeg + 90;
    }

    // Set rotation via CSS custom property (CSP-compliant)
    image.style.setProperty("--image-rotation", `${newDeg}deg`);
  };
}

export class VideoPreview {
  constructor({
    previewInfo,
    parent,
    controls = false,
    autoplay = false,
    disableContextMenu = false,
  }) {
    const { href_path, mediaInfo, preview } = previewInfo;

    const source = `/?command=download&mimeType=${
      mediaInfo?.mimeType || "video"
    }&path=${href_path}&c2f=${utils.cookie.get("currentAuth")}`;

    const coverImageSource =
      preview > 0
        ? `/?command=getPreview&size=3&path=${href_path}&frame=1&c2f=${utils.cookie.get(
            "currentAuth"
          )}`
        : ``;

    this.container = _$("<div>").addClass("video-preview-container").el();
    const video = _$("<video>").addClass("video-js").attr("preload", "auto");
    
    // Ensure video element is visible and has proper dimensions
    video.addClass("d-b");
    video.removeClass("d-n");

    if (coverImageSource) {
      video.attr("poster", coverImageSource);
    }

    this.videoElement = video.el();
    
    // Ensure video element has display block and visibility visible via CSSOM
    if (this.videoElement) {
      this.videoElement.style.setProperty("display", "block");
      this.videoElement.style.setProperty("visibility", "visible");
      this.videoElement.style.setProperty("opacity", "1");
    }

    if (disableContextMenu) {
      this.videoElement.addEventListener("contextmenu", (event) => {
        event.preventDefault();
      });
    }

    _$(this.container).append(this.videoElement);
    _$(parent).append(this.container);

    // Destroy existing player if it exists
    if (this.player) {
      this.player.dispose();
    }

    this.player = videojs(this.videoElement, {
      controls: controls,
      autoplay: autoplay,
      preload: "auto",
      sources: [{ src: `${source}#.mp4` }],
      html5: {
        nativeTextTracks: false,
      },
      skipButtons: false,
      controlBar: {
        liveDisplay: false,
        seekToLive: false,
        playbackRateMenuButton: false,
        chaptersButton: false,
        descriptionsButton: false,
        audioTrackButton: false,
        pictureInPictureToggle: false,
        skipButtons: { forward: false, backward: false },
      },
    });
    this.attachSidecarSubtitles(previewInfo);

    // Ensure video is visible after video.js initialization
    if (this.player && this.player.el_) {
      const playerEl = this.player.el();
      if (playerEl) {
        playerEl.style.setProperty("display", "block");
        playerEl.style.setProperty("visibility", "visible");
        playerEl.style.setProperty("opacity", "1");
        
        // Find the actual video element inside the video.js wrapper
        const actualVideo = playerEl.querySelector("video") || playerEl.querySelector(".vjs-tech") || this.videoElement;
        if (actualVideo && actualVideo !== playerEl) {
          actualVideo.style.setProperty("display", "block");
          actualVideo.style.setProperty("visibility", "visible");
          actualVideo.style.setProperty("opacity", "1");
          actualVideo.style.setProperty("width", "100%");
          actualVideo.style.setProperty("height", "auto");
          actualVideo.style.setProperty("max-width", "100%");
          actualVideo.style.setProperty("max-height", "100%");
        }
      }
    }

    video.on("loadedmetadata", () => {
      const height = this.videoElement.videoHeight;
      const width = this.videoElement.videoWidth;

      const orientation = height > width ? "portrait" : "landscape";
      video.addClass(orientation);
      
      // Ensure video remains visible after metadata loads
      if (this.videoElement) {
        this.videoElement.style.setProperty("display", "block");
        this.videoElement.style.setProperty("visibility", "visible");
        this.videoElement.style.setProperty("opacity", "1");
        this.videoElement.style.setProperty("width", "100%");
        this.videoElement.style.setProperty("height", "auto");
      }
      
      // Also ensure the player wrapper and inner video are visible
      if (this.player && this.player.el_) {
        const playerEl = this.player.el();
        const actualVideo = playerEl.querySelector("video") || playerEl.querySelector(".vjs-tech") || this.videoElement;
        if (actualVideo && actualVideo !== playerEl) {
          actualVideo.style.setProperty("display", "block");
          actualVideo.style.setProperty("visibility", "visible");
          actualVideo.style.setProperty("opacity", "1");
          actualVideo.style.setProperty("width", "100%");
          actualVideo.style.setProperty("height", "auto");
        }
      }
    });

    this.errorMessage = "";
    this.hasError = false;
    this.setupEventListeners();
  }

  setupEventListeners() {
    this.player.on("loadstart", () => this.showLoader());
    this.player.on("canplay", () => this.hideLoader());
    // this.player.on("error", () => this.handleVideoError());
  }

  showLoader() {
    this.player.addClass("vjs-waiting");
  }

  hideLoader() {
    this.player.removeClass("vjs-waiting");
  }

  handleVideoError() {
    console.error("Video playback failed:", this.player.error());
    this.errorMessage = `Video playback failed: ${this.player.error().message}`;
    this.hasError = true;
    this.showErrorToast();
  }

  showErrorToast = () => {
    toast({
      type: "error",
      title: "Video Playback Error",
      message: this.errorMessage,
      autoClose: true,
      delay: 5000,
    });
  };

  dispose() {
    if (this.player) {
      this.player.dispose();
      this.player = null;
    }
  }

  escapeRegExp = (value) =>
    String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  normalizeDirPath = (path) => {
    const decoded = utils.decodeURILocal(path || "");
    return decoded.replace(/\/+$/, "").toLowerCase();
  };

  getFileDir = (file) => {
    const root = file?.root_dir;
    if (root) return this.normalizeDirPath(root);
    const href = file?.href_path || "";
    const idx = href.lastIndexOf("/");
    return this.normalizeDirPath(idx >= 0 ? href.slice(0, idx) : href);
  };

  srtToVtt = (srtText) => {
    const body = String(srtText || "")
      .replace(/^\uFEFF/, "")
      .replace(/\r+/g, "")
      .replace(/(\d+:\d+:\d+),(\d+)/g, "$1.$2")
      .trim();
    if (/^WEBVTT/i.test(body)) return body;
    return `WEBVTT\n\n${body}`;
  };

  parseTimestamp = (value) => {
    const ts = String(value || "").replace(",", ".");
    const parts = ts.split(":");
    if (parts.length === 3) {
      return Number(parts[0]) * 3600 + Number(parts[1]) * 60 + Number(parts[2]);
    }
    if (parts.length === 2) {
      return Number(parts[0]) * 60 + Number(parts[1]);
    }
    return Number(ts) || 0;
  };

  parseVttCues = (vttText) => {
    const text = String(vttText || "")
      .replace(/^\uFEFF/, "")
      .replace(/\r+/g, "");
    const blocks = text.split(/\n\n+/);
    const cues = [];
    blocks.forEach((block) => {
      const lines = block.split("\n").filter((line) => {
        const trimmed = line.trim();
        return (
          trimmed &&
          !/^WEBVTT/i.test(trimmed) &&
          !/^NOTE\b/i.test(trimmed) &&
          !/^STYLE\b/i.test(trimmed) &&
          !/^REGION\b/i.test(trimmed)
        );
      });
      const timeIndex = lines.findIndex((line) => /-->/.test(line));
      if (timeIndex < 0) return;
      const timeMatch = lines[timeIndex].match(
        /(\d{1,2}:\d{2}(?::\d{2})?[.,]\d{1,3})\s*-->\s*(\d{1,2}:\d{2}(?::\d{2})?[.,]\d{1,3})/
      );
      if (!timeMatch) return;
      const cueText = lines.slice(timeIndex + 1).join("\n").trim();
      if (!cueText) return;
      cues.push({
        start: this.parseTimestamp(timeMatch[1]),
        end: this.parseTimestamp(timeMatch[2]),
        text: cueText,
      });
    });
    return cues;
  };

  findMatchingSubtitleFiles = (previewInfo) => {
    const files = store.files || [];
    const videoName = previewInfo?.name || "";
    const lastDot = videoName.lastIndexOf(".");
    if (lastDot <= 0) return [];
    const basename = videoName.slice(0, lastDot);
    if (!basename) return [];
    const videoDir = this.getFileDir(previewInfo);
    const pattern = new RegExp(
      `^${this.escapeRegExp(basename)}(?:\\.([a-zA-Z]{2,3}(?:-[a-zA-Z]{2,4})?))?\\.(vtt|srt)$`,
      "i"
    );
    const matches = [];
    files.forEach((file) => {
      if (!file || file.type === fileTypes.DIR) return;
      if (this.getFileDir(file) !== videoDir) return;
      const captured = (file.name || "").match(pattern);
      if (!captured) return;
      matches.push({
        file,
        srclang: captured[1] || "und",
        label: captured[1] || t("subtitles"),
        ext: captured[2].toLowerCase(),
      });
    });
    return matches;
  };

  fetchSubtitleText = async (match) => {
    const path = match.file.href_path;
    const c2f = utils.cookie.get("currentAuth");
    const mimeType = match.ext === "srt" ? "text/plain" : "text/vtt";
    const url = `/?command=download&mimeType=${mimeType}&path=${path}&c2f=${c2f}`;
    const response = await fetch(url);
    if (!response.ok) return "";
    return response.text();
  };

  attachSidecarSubtitles = async (previewInfo) => {
    if (!this.player) return;
    const subtitleCustomization =
      store.userInfo?.userCustomizations?.showVideoPlayerSubtitles;
    if (subtitleCustomization && subtitleCustomization.value === "false") {
      return;
    }
    const matches = this.findMatchingSubtitleFiles(previewInfo);
    let enabledTrack = false;
    for (const match of matches) {
      if (!this.player) return;
      try {
        const rawText = await this.fetchSubtitleText(match);
        if (!rawText || !this.player) continue;
        const vtt = match.ext === "srt" ? this.srtToVtt(rawText) : rawText;
        const cues = this.parseVttCues(vtt);
        if (!cues.length) continue;
        const track = this.player.addTextTrack(
          "subtitles",
          match.label,
          match.srclang
        );
        if (!track) continue;
        cues.forEach((cue) => {
          track.addCue(new VTTCue(cue.start, cue.end, cue.text));
        });
        track.mode = enabledTrack ? "disabled" : "showing";
        enabledTrack = true;
      } catch (e) {}
    }
  };
}

export class AudioPreview {
  constructor({ previewInfo, parent }) {
    const { href_path } = previewInfo;

    this.loader = _$("<div>").addClass("media-loader").el();
    _$(parent).append(this.loader);

    this.waveContainer = _$("<div>").el();
    this.waveContainer.id = "waveform";

    this.audio = _$("<audio>").attr("src", href_path).el();
    const audio = this.audio;

    _$(parent).append(this.waveContainer);
    _$(parent).append(this.audio);

    // Function to get computed CSS variable value
    function getCSSVariableValue(variableName) {
      return getComputedStyle(document.documentElement)
        .getPropertyValue(variableName)
        .trim();
    }

    // Get color values
    const progressColor = getCSSVariableValue("--accent-color");
    const waveColor = getCSSVariableValue("--border-color");
    const cursorColor = getCSSVariableValue("--border-color");

    const waveSurfer = WaveSurfer.create({
      container: "#waveform",
      barWidth: 1,
      barGap: 1,
      height: 100,
      barRadius: 2,
      progressColor: progressColor,
      waveColor: waveColor,
      cursorColor: cursorColor,
      backend: "MediaElement",
      hideScrollbar: true,
      fillParent: true,
      responsive: true,
    });

    waveSurfer.load(audio);

    this.showLoader(); // Show loader immediately
    this.setupEventListeners();
    this.setupWaveSurfer(waveSurfer);
  }

  setupEventListeners() {
    this.audio.addEventListener("loadstart", () => this.showLoader());
    this.audio.addEventListener("canplay", () => this.hideLoader());
    this.audio.addEventListener("error", () => this.handleAudioError());
  }

  showLoader() {
    _$(this.loader).show();
  }

  hideLoader() {
    _$(this.loader).hide();
  }

  handleAudioError() {
    console.log("Audio loading failed");
    this.hideLoader();
  }

  setupWaveSurfer(waveSurfer) {
    waveSurfer.on("ready", () => this.hideLoader());
  }
}
